import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BadgeDollarSign, Loader2, Gift, Banknote, TrendingUp } from "lucide-react";
import MerchantApplyModal from "@/components/MerchantApplyModal";

const STATUS_COLORS = { pending: "#FACC15", paid: "#4ADE80", denied: "#F87171" };

export default function RedemptionPortal({ balance, onRedeemed }) {
  const [cfg, setCfg] = useState(null);
  const [mine, setMine] = useState([]);
  const [merchant, setMerchant] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("credit");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/redemption/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/redemption/mine").then(({ data }) => setMine(data)).catch(() => {});
    api.get("/merchant/me").then(({ data }) => setMerchant(data)).catch(() => {});
  };
  useEffect(load, []);

  if (!cfg || !merchant || (!cfg.enabled && mine.length === 0)) return null;
  const usd = (Number(amount) || 0) * cfg.rate_usd;
  const isApproved = merchant.status === "approved";

  const redeem = async () => {
    setBusy(true);
    try {
      await api.post("/redemption/request", { blc: Number(amount), method });
      toast.success(`Redemption submitted — ${amount} BLC ($${usd.toFixed(2)}) pending approval`);
      setAmount("");
      load();
      onRedeemed?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-pb-surface border border-amber-400/40 p-6 mb-8" data-testid="redemption-portal">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <BadgeDollarSign className="w-4 h-4 text-amber-400" />
          <span className="font-display font-bold text-lg uppercase">BLC Redemption Portal</span>
        </div>
        {cfg.enabled && (
          <div className="flex items-center gap-2 border border-amber-400/50 bg-amber-400/10 px-3 py-1.5" data-testid="redemption-rate-box">
            <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">
              Today's Rate: ${cfg.rate_usd} / BLC
            </span>
          </div>
        )}
      </div>
      {cfg.enabled ? (isApproved ? (
        <>
          <p className="text-xs text-zinc-500 mb-4">
            Turn your earned coins into real value — the rate is set daily and can change at any time. Minimum {cfg.min_blc} BLC.
            Bonus tokens can't be redeemed. Coins are held when you submit and returned if the request is denied.
          </p>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">BLC to redeem</label>
              <input data-testid="redemption-amount" type="number" min={cfg.min_blc} step={1} value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder={`${cfg.min_blc}+`}
                className="w-28 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono text-sm focus:outline-none focus:border-amber-400" />
            </div>
            <div className="flex gap-1.5">
              <button data-testid="redemption-method-credit" onClick={() => setMethod("credit")}
                className={`flex items-center gap-1.5 px-3 py-2.5 border text-[10px] font-black uppercase tracking-widest transition-colors ${
                  method === "credit" ? "border-amber-400 bg-amber-400/10 text-amber-400" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
                <Gift className="w-3.5 h-3.5" /> Store Credit
              </button>
              <button data-testid="redemption-method-cash" onClick={() => setMethod("cash")}
                className={`flex items-center gap-1.5 px-3 py-2.5 border text-[10px] font-black uppercase tracking-widest transition-colors ${
                  method === "cash" ? "border-emerald-400 bg-emerald-400/10 text-emerald-400" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
                <Banknote className="w-3.5 h-3.5" /> Cash Payout
              </button>
            </div>
            <button data-testid="redemption-submit" onClick={redeem}
              disabled={busy || !amount || Number(amount) < cfg.min_blc || (balance != null && Number(amount) > balance)}
              className="flex items-center gap-2 bg-amber-400 text-black font-black uppercase tracking-widest px-6 py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeDollarSign className="w-4 h-4" />}
              Redeem{usd > 0 ? ` — $${usd.toFixed(2)}` : ""}
            </button>
          </div>
        </>
      ) : (
        <div className="border border-pb-border bg-pb-elevated/40 p-4 mb-3" data-testid="redemption-merchant-gate">
          <p className="text-xs text-zinc-300 mb-1 font-semibold">
            Settlement payouts are for approved Merchants &amp; Card Merchants only.
          </p>
          <p className="text-[11px] text-zinc-500 mb-3">
            {merchant.status === "pending"
              ? "Your merchant application is pending review — we'll notify you the moment you're approved."
              : merchant.status === "suspended"
              ? "Your merchant account is suspended. Contact the Bullpen team for details."
              : "Coins stay spendable everywhere in the Bullpen. To redeem earned coins for payouts, apply for a merchant account — card sellers should pick Card Merchant."}
          </p>
          {(merchant.status === "none" || merchant.status === "rejected") && (
            <button data-testid="redemption-apply-merchant-btn" onClick={() => setApplyOpen(true)}
              className="flex items-center gap-2 bg-amber-400 text-black font-black uppercase tracking-widest px-5 py-2.5 text-[10px] hover:bg-[#BEF264] transition-colors">
              <Gift className="w-3.5 h-3.5" /> Apply to Become a Merchant
            </button>
          )}
        </div>
      )) : (
        <p className="text-xs text-zinc-500 mb-3">The Redemption Portal is currently closed — your past requests are below.</p>
      )}
      {mine.length > 0 && (
        <div className="divide-y divide-pb-border/40 border border-pb-border mt-2" data-testid="redemption-history">
          {mine.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`redemption-row-${r.id}`}>
              <span className="text-zinc-300">{r.blc} BLC → ${r.usd.toFixed(2)} <span className="text-zinc-600">({r.method === "credit" ? "store credit" : "cash"} @ ${r.rate})</span></span>
              <span className="flex items-center gap-3">
                <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: STATUS_COLORS[r.status] }}>{r.status}</span>
                <span className="text-zinc-600">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
              </span>
            </div>
          ))}
        </div>
      )}
      <MerchantApplyModal open={applyOpen} onClose={() => setApplyOpen(false)} presetType="card_merchant" onDone={load} />
    </div>
  );
}
